// ─────────────────────────────────────────────────────────────────────────
// Shared server-connectivity check + IT alert helper.
//
// Built as part of MEJORA 2 (price validation), which needs it to double
// check the server/catalog is trustworthy before showing a price warning.
//
// TODO (MEJORA 1): this module is the shared foundation for the broader
// connectivity feature — reuse it (don't duplicate this logic) when we land:
//   - InvoiceV3 componentDidMount: fire `ensureServerHealthy()` once in the
//     background when the screen loads, so the cache is warm by the time the
//     user clicks anything.
//   - "New Invoice" button: call `ensureServerHealthy()` before requesting
//     the correlative invoice number. On definitive failure, show the
//     "Offline mode" banner and switch the primary action button to
//     "Save temporal" (saves to the AWS pending-sync table instead of the
//     normal flow) with a TEMP-<timestamp> placeholder InvoiceNo.
//   - "Sales Order list" / "Invoice list" buttons: call `ensureServerHealthy()`
//     unconditionally before loading the detail (this is separate from the
//     price-triggered call already wired into InvoiceV3.priceValidation()).
//   - New "pending sync" button: simple list + status (pending/synced/error),
//     backed by a new (TODO) endpoint — no actions yet in v1.
// ─────────────────────────────────────────────────────────────────────────

import { getInformationWithData } from '../services/CABE';

const CACHE_KEY = 'ServerLastCheck';
const SUCCESS_TTL_MS = 5 * 60 * 1000; // 5 min — reused across all trigger points
const FAILURE_TTL_MS = 2 * 60 * 1000; // short on purpose: detect recovery fast
// 8s turned out to be too tight for this specific endpoint (it does real
// work against MAS90/SAGE, not a lightweight ping) — axios was aborting the
// request before the server could respond at all, which Chrome's Network
// tab shows as "(canceled)" with no error text, easy to mistake for a
// manual cancel. 20s gives it real room to answer while still being well
// under the generic 30s REQUEST_TIMEOUT used everywhere else.
const ATTEMPT_TIMEOUT_MS = 20000;
const RETRY_DELAY_MS = 5000;

// Real endpoint, provided as-is for now (it's a "Test/EnvioOrden" route being
// reused as a stand-in health-check — expect this to be swapped for a
// purpose-built one later). Response contract is flat, NOT the usual
// { status: { code }, data: [...] } shape:
//   healthy   -> { code: 1,  Description: "operation successful" }
//   unhealthy -> { code: -1, Description: "..." }
// Network errors / timeouts / non-2xx already get normalized by
// getInformationWithData's catch block into an object that also carries a
// `code` field (but a string like 'TIMEOUT'/'NETWORK', or null) — so a plain
// `result.code === 1` check below correctly treats those as unhealthy too.
const HEALTH_CHECK_ROUTE = '/Test/EnvioOrden';
const HEALTH_CHECK_PAYLOAD = { OrderType: 'CheckServerHealth' };

// NOTE: there is no "restart" endpoint — if the server can't be reached,
// there's no way to command it to restart either (you can't message
// something you can't connect to). The only automated action possible is
// notifying a human (IT) so they restart it manually — see notifyIT() below,
// called automatically from ensureServerHealthy() once all attempts fail.

// TODO (BACKEND): confirm real route + payload. Expected to send an email +
// a Telegram message to the IT team.
const NOTIFY_IT_ROUTE = '/TODO-MEJORA1/notify-it';

const IT_ALERT_COOLDOWN_KEY = 'ITAlertLastSent';
const IT_ALERT_COOLDOWN_MS = 5 * 60 * 1000;
// TODO (BACKEND): this client-side cooldown is only an interim safety net so
// one browser doesn't spam IT on every retry — the real dedupe should live
// server-side so it also covers different users/browsers hitting the same
// incident at once. Relax/remove this once that exists.

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

function writeCache(ok) {
    const ttl = ok ? SUCCESS_TTL_MS : FAILURE_TTL_MS;
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
            ok,
            checkedAt: Date.now(),
            validUntil: Date.now() + ttl,
        }));
    } catch (e) {
        // localStorage unavailable (private mode, quota, etc.) — degrade to
        // "always check", not a hard failure.
    }
}

// { valid: false } if there's no usable cached result yet (missing/expired).
// { valid: true, ok } if we can trust the cached result instead of checking again.
export function getCachedServerStatus() {
    const cache = readCache();
    if (!cache) return { valid: false };
    if (Date.now() > cache.validUntil) return { valid: false };
    return { valid: true, ok: cache.ok };
}

// Forces the next ensureServerHealthy() call (from anywhere) to check fresh
// instead of trusting a stale cached result — used after a manual "Retry"
// still fails, so the short failure-TTL doesn't get in the way right after.
export function clearServerCheckCache() {
    try {
        localStorage.removeItem(CACHE_KEY);
    } catch (e) {
        // ignore
    }
}

async function singleHealthCheckAttempt() {
    const result = await getInformationWithData(HEALTH_CHECK_ROUTE, HEALTH_CHECK_PAYLOAD, ATTEMPT_TIMEOUT_MS);
    return !!(result && result.code === 1);
}

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Ensures the server is reachable, reusing the shared 5-min/2-min cache
 * whenever possible so repeated actions (New Invoice, Sales Order list,
 * Invoice list, price validation) don't each re-check independently.
 *
 * Escalation (only runs when the cache is missing/expired):
 *   1. one silent attempt, no modal
 *   2-4. up to 3 more attempts, 5s apart, showing progress text via loadingRef
 *
 * There is no automated "restart" step — if all 4 attempts fail, the only
 * thing the system can do is notify IT (email + Telegram) so a human
 * restarts the server manually; that alert fires automatically here, so
 * every caller gets it for free without having to remember to trigger it.
 *
 * @param {Object} options
 * @param {React.RefObject} [options.loadingRef] - ref to a <LoadingWindow />
 *        instance; used to surface the "Checking..." / "2nd try..." /
 *        "Last try..." progress modals. Optional — pass it whenever the
 *        caller already renders a <LoadingWindow />.
 * @param {boolean} [options.forceRecheck] - ignore the cache and check again
 * @param {Object} [options.notifyContext] - extra info merged into the IT
 *        alert's context payload if this call ends up failing (e.g. which
 *        SalesOrderNo/InvoiceNo the user was working on).
 * @returns {Promise<boolean>} true if the server ended up healthy
 */
export async function ensureServerHealthy({ loadingRef, forceRecheck = false, notifyContext } = {}) {
    if (!forceRecheck) {
        const cached = getCachedServerStatus();
        if (cached.valid) {
            // Bug fix: a caller may have already shown its own loading state
            // on this same ref before calling us (e.g. "Verifying prices...")
            // — if we return here without touching it, it stays stuck
            // visible forever, since nothing else turns it off.
            if (loadingRef && loadingRef.current) loadingRef.current.showState(false);
            return cached.ok;
        }
    }

    // Attempt 1 — silent, no modal (this is the common/happy path).
    if (await singleHealthCheckAttempt()) {
        writeCache(true);
        if (loadingRef && loadingRef.current) loadingRef.current.showState(false);
        return true;
    }

    const progressMessages = [
        'Checking the server connection. Please wait',
        '2nd try to check the server connection. Please wait',
        'Last try to check the server connection. Please wait',
    ];

    let healthy = false;
    for (let i = 0; i < progressMessages.length; i++) {
        if (loadingRef && loadingRef.current) {
            loadingRef.current.showState(true, progressMessages[i]);
        }
        await wait(RETRY_DELAY_MS);

        if (await singleHealthCheckAttempt()) {
            healthy = true;
            break;
        }
    }

    if (loadingRef && loadingRef.current) {
        loadingRef.current.showState(false);
    }

    writeCache(healthy);

    if (!healthy) {
        // Best-effort, debounced — a human needs to restart the server, this
        // is the only way the system can ask for that.
        notifyIT({ type: 'connectivity_failure', context: notifyContext });
    }

    return healthy;
}

/**
 * Notifies IT (email + Telegram, per backend) that the portal couldn't reach
 * the server, or found a data-integrity issue worth a human look. Debounced
 * client-side so one user's retries don't spam multiple messages.
 *
 * @param {Object} params
 * @param {string} params.type - e.g. 'connectivity_failure' | 'missing_catalog_item'
 * @param {Object} [params.context] - free-form extra info for the ticket/log
 */
export async function notifyIT({ type, context } = {}) {
    const lastSent = Number(localStorage.getItem(IT_ALERT_COOLDOWN_KEY) || 0);
    if (Date.now() - lastSent < IT_ALERT_COOLDOWN_MS) return;

    try {
        localStorage.setItem(IT_ALERT_COOLDOWN_KEY, String(Date.now()));
    } catch (e) {
        // ignore — worst case we send a duplicate alert
    }

    try {
        await getInformationWithData(NOTIFY_IT_ROUTE, { type, context });
    } catch (e) {
        // best-effort — never block the user's flow over a failed IT alert
    }
}
